import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const APPLIANCES = [
  { id: "agaro-espresso-coffee-maker", name: "Espresso Coffee Maker", line: "Brew a Brighter Day" },
  { id: "agaro-regency-air-fryer-65", name: "Regency Digital Air Fryer 6.5L", line: "Good Food, Brighter Days" },
  { id: "agaro-imperial-slow-juicer", name: "Imperial Slow Juicer", line: "Pure Goodness, Every Day" },
  { id: "agaro-nutriblend-mixer-grinder", name: "NutriBlend Mixer Grinder 1000W", line: "Blend · Grind · Chop · More" },
  { id: "agaro-elitemix-mixer-grinder", name: "EliteMix Mixer Grinder 750W", line: "Grind More. Cook Better." },
  { id: "agaro-galaxy-air-fryer-45", name: "Galaxy Digital Air Fryer 4.5L", line: "Healthier Meals, Happier Moments" },
  { id: "agaro-galaxy-nutri-blender", name: "Galaxy Nutri Blender", line: "Smart Living for a Better You" },
  { id: "agaro-regency-multi-kettle", name: "Regency Multi Kettle 1.2L", line: "More Than a Kettle" },
  { id: "agaro-marvel-otg-28l", name: "Marvel OTG 28L", line: "Bake · Grill · Toast · Roast" },
  { id: "agaro-ovenchef-air-fryer-oven", name: "OvenChef Air Fryer Oven 12L", line: "Air Fry · Bake · Roast · Grill · Dehydrate" },
  { id: "agaro-hand-mixer", name: "Hand Mixer", line: "Bake Happier Everyday" },
];

export default function AgaroCollection() {
  useEffect(() => {
    document.title = "AGARO Collection — THE GOOD GRID";
  }, []);

  return (
    <div data-testid="agaro-collection-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>AGARO × THE GOOD GRID</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-4 tracking-tight max-w-4xl leading-[1.05]">
            Smart living,<br /><span className="italic text-champagne-light">gifted well.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/50 mt-6 max-w-xl leading-relaxed">
            Premium kitchen appliances and cookware from AGARO — air fryers, espresso makers, slow juicers, mixer grinders and tri-ply cookware, curated for employee programmes, festive hampers and executive gifting.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <h2 className="font-serif text-2xl sm:text-3xl mt-16 mb-8 tracking-tight text-ivory/90">The Appliance Range</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {APPLIANCES.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08}>
              <Link
                to={`/product/${c.id}`}
                data-testid={`agaro-card-${c.id}`}
                className="group relative block overflow-hidden border border-white/10"
              >
                <img
                  src={`/products/${c.id}.jpg`}
                  alt={`AGARO ${c.name}`}
                  loading="lazy"
                  className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#121212]/85 via-transparent to-transparent p-5 flex items-end justify-between gap-3">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-ivory/70 italic font-serif normal-case">{c.line}</span>
                  <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-ivory bg-[#121212]/70 backdrop-blur-sm border border-white/15 px-3.5 py-2 group-hover:border-champagne group-hover:text-champagne transition-colors duration-300 shrink-0">
                    View <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-24">
          <Reveal><Eyebrow>Cookware & Glassware</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-5xl mt-4 mb-10 tracking-tight">From kitchen <span className="italic text-champagne-light">to table.</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { id: "agaro-triply-cookware-set", name: "Tri-Ply Cookware Set", line: "Cook · Serve · Inspire" },
              { id: "agaro-elitecook-cookware-collection", name: "EliteCook Cookware Collection", line: "Cook · Serve · Savour" },
              { id: "agaro-crystalsip-glassware", name: "CrystalSip Premium Glassware", line: "Sip · Serve · Celebrate" },
            ].map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 0.08}>
                <Link
                  to={`/product/${c.id}`}
                  data-testid={`agaro-card-${c.id}`}
                  className="group relative block overflow-hidden border border-white/10"
                >
                  <img
                    src={`/products/${c.id}.jpg`}
                    alt={`AGARO ${c.name}`}
                    loading="lazy"
                    className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#121212]/85 via-transparent to-transparent p-5 flex items-end justify-between gap-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-ivory/70 italic font-serif normal-case">{c.line}</span>
                    <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-ivory bg-[#121212]/70 backdrop-blur-sm border border-white/15 px-3.5 py-2 group-hover:border-champagne group-hover:text-champagne transition-colors duration-300 shrink-0">
                      View <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20 border border-champagne/30 bg-[#151210] p-8 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal><Eyebrow>Festive & Bulk Programmes</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-5xl mt-4 tracking-tight">Festive bundle pricing, <span className="italic text-champagne-light">on request.</span></h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-ivory/50 mt-5 max-w-2xl text-sm leading-relaxed">
                Build Diwali, New Year and onboarding programmes around AGARO appliances and cookware — mixed bundles, custom sleeves, message cards and multi-city dispatch. Share your quantity and budget per gift; we return a curated proposal.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.25}>
              <Link
                to="/request-quote"
                data-testid="agaro-bundle-quote"
                className="group flex items-center justify-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-5 hover:bg-champagne-light transition-colors duration-300 w-full"
              >
                Request Bundle Quote <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <p className="text-ivory/30 text-xs mt-4 text-center">Response within one business day.</p>
            </Reveal>
          </div>
        </div>

        <p className="text-ivory/30 text-xs mt-10">AGARO is a brand of Universal Corporation. Products are sourced and curated by THE GOOD GRID for corporate gifting.</p>
      </div>
    </div>
  );
}
